import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import { authFetch } from '../utils/api';
import { buildSbomCandidates } from '../utils/sbom';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';

const decisionTone = {
  preview_passed: 'success',
  needs_review: 'warning',
  blocked: 'danger',
};

export default function IntelligenceView({ repoName, sbomId, sbomName }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [ruleOptions, setRuleOptions] = useState([]);
  const [selectedRuleId, setSelectedRuleId] = useState('sql-injection-public-api');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [repoPath, setRepoPath] = useState('');
  const endpointUnavailableMessage = t('intelligencePreview.endpointUnavailable');

  const fetchRuleOptions = useCallback(async () => {
    const response = await authFetch(`${config.BASE_URL}/intelligence/rulepacks`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const payload = await response.json();
    const rules = (payload.packs || []).flatMap((pack) => (pack.rules || []).map((rule) => ({
      id: rule.id,
      name: rule.name || rule.id,
      status: rule.status || '',
      owner: rule.owner || '',
      category: rule.category || '',
      severity: rule.severity || '',
      defaultConfidence: rule.defaultConfidence || '',
      languages: rule.languages || [],
      tags: rule.tags || [],
      cwe: rule.cwe || [],
      owasp: rule.owasp || [],
      remediationKinds: rule.remediationKinds || [],
      evidenceKinds: rule.evidenceKinds || [],
      remediation: rule.remediation || '',
      description: rule.description || '',
      packId: pack.id,
      packName: pack.name || pack.id,
    })));
    return rules;
  }, []);

  const fetchHistory = useCallback(async (ruleId) => {
    const candidates = buildSbomCandidates(sbomId, sbomName);
    const query = ruleId ? `?ruleId=${encodeURIComponent(ruleId)}` : '';

    for (const candidate of candidates) {
      const response = await authFetch(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(candidate)}/intelligence/history${query}`);
      if (response.ok) {
        const payload = await response.json();
        return payload.history || [];
      }
      if (response.status === 404) {
        continue;
      }
      throw new Error(`HTTP ${response.status}`);
    }

    return [];
  }, [repoName, sbomId, sbomName]);

  const loadPreview = useCallback(async ({ preserveData = false, scanId = '', ruleId = '' } = {}) => {
    const candidates = buildSbomCandidates(sbomId, sbomName);
    let sawNotFound = false;
    if (!preserveData) {
      setData(null);
    }

    for (const candidate of candidates) {
      const params = new URLSearchParams();
      if (scanId) params.set('scanId', scanId);
      if (ruleId) params.set('ruleId', ruleId);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await authFetch(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(candidate)}/intelligence${query}`);
      if (response.ok) {
        const payload = await response.json();
        setData(payload);
        setRepoPath(payload.repoPath || '');
        return payload;
      }
      if (response.status === 404) {
        sawNotFound = true;
        continue;
      }
      throw new Error(`HTTP ${response.status}`);
    }

    if (sawNotFound) {
      setData(null);
      return null;
    }

    throw new Error(endpointUnavailableMessage);
  }, [endpointUnavailableMessage, repoName, sbomId, sbomName]);

  useEffect(() => {
    if (!repoName || !sbomId) return;

    let cancelled = false;
    setLoading(true);
    setError('');
    setData(null);
    setHistory([]);

    Promise.all([fetchRuleOptions(), loadPreview({ ruleId: selectedRuleId }), fetchHistory(selectedRuleId)])
      .then(([rulesPayload, previewPayload, historyPayload]) => {
        if (cancelled) return;
        setRuleOptions(rulesPayload || []);
        if ((rulesPayload || []).length > 0 && !(rulesPayload || []).some((rule) => rule.id === selectedRuleId)) {
          setSelectedRuleId(rulesPayload[0].id);
          return;
        }
        setHistory(historyPayload || []);
        if (!previewPayload && (historyPayload || []).length > 0) {
          loadPreview({ preserveData: false, scanId: historyPayload[0].scanId, ruleId: selectedRuleId }).catch((e) => {
            if (!cancelled) setError(e.message);
          });
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchHistory, loadPreview, fetchRuleOptions, repoName, sbomId, selectedRuleId]);

  const triggerLivePreview = async () => {
    setRunning(true);
    setError('');
    try {
      const candidates = buildSbomCandidates(sbomId, sbomName);
      let lastError = null;
      for (const candidate of candidates) {
        const response = await authFetch(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(candidate)}/intelligence`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ repoPath: repoPath.trim(), ruleId: selectedRuleId }),
        });
        if (response.ok) {
          const payload = await response.json();
          setData(payload);
          setRepoPath(payload.repoPath || repoPath.trim() || '');
          const historyPayload = await fetchHistory(selectedRuleId);
          setHistory(historyPayload || []);
          return;
        }
        if (response.status !== 404) {
          lastError = new Error(`HTTP ${response.status}`);
          break;
        }
      }
      throw lastError || new Error(endpointUnavailableMessage);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const selectHistoryRun = async (scanId) => {
    setLoading(true);
    setError('');
    try {
      await loadPreview({ preserveData: false, scanId, ruleId: selectedRuleId });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedRule = useMemo(
    () => ruleOptions.find((rule) => rule.id === selectedRuleId) || null,
    [ruleOptions, selectedRuleId]
  );
  const preview = data?.preview;
  const findings = useMemo(() => preview?.execution?.findingDetails || [], [preview]);
  const assertionTotal = (preview?.evaluation?.expectedAssertionsPassed ?? 0) + (preview?.evaluation?.expectedAssertionsFailed ?? 0);

  if (loading) {
    return (
      <div style={{ padding: '1rem 0' }}>
        <Skeleton.Card />
        <div className="small text-secondary mt-3">{t('intelligencePreview.loading')}</div>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{t('intelligencePreview.loadError')}: {error}</div>;
  }

  if (!preview) {
    return (
      <EmptyState
        icon="🧠"
        title={t('intelligencePreview.emptyTitle')}
        description={t('intelligencePreview.emptyDescription')}
      />
    );
  }

  return (
    <div className="d-flex flex-column gap-4">
      <div className="ce-card">
        <div className="ce-card-header">
          <div className="ce-section-header">
            <span className="ce-card-title">🧠 {t('intelligencePreview.title')}</span>
            <div className="ce-section-actions">
              {data?.demo
                ? <span className="ce-sev ce-sev--info">{t('intelligencePreview.demoBadge')}</span>
                : <span className="ce-sev ce-sev--low">{t('intelligencePreview.liveBadge')}</span>}
              <span className={`ce-sev ce-sev--${decisionTone[preview.promotionDecision] === 'success' ? 'low' : decisionTone[preview.promotionDecision] === 'warning' ? 'medium' : decisionTone[preview.promotionDecision] === 'danger' ? 'critical' : 'none'}`}>
                {preview.promotionDecision || 'unknown'}
              </span>
            </div>
          </div>
          <div className="small text-secondary">{t('intelligencePreview.subtitle')}</div>
        </div>
        <div className="ce-card-body">

          <div className="row g-3 mt-1">
            <div className="col-12 col-lg-4">
              <label className="form-label small fw-semibold">{t('intelligencePreview.ruleSelectorLabel')}</label>
              <select
                className="form-select"
                value={selectedRuleId}
                onChange={(e) => setSelectedRuleId(e.target.value)}
                disabled={loading || running || ruleOptions.length === 0}
              >
                {ruleOptions.map((rule) => (
                  <option key={rule.id} value={rule.id}>
                    {rule.name}
                  </option>
                ))}
              </select>
              <div className="form-text">
                {selectedRule
                  ? t('intelligencePreview.ruleSelectorHint', { packName: selectedRule.packName, severity: selectedRule.severity || '—' })
                  : t('intelligencePreview.ruleSelectorEmpty')}
              </div>
            </div>
            <div className="col-12 col-lg-5">
              <label className="form-label small fw-semibold">{t('intelligencePreview.repoPathLabel')}</label>
              <input
                className="form-control"
                value={repoPath}
                onChange={(e) => setRepoPath(e.target.value)}
                placeholder={t('intelligencePreview.repoPathPlaceholder')}
              />
              <div className="form-text">{t('intelligencePreview.repoPathHint')}</div>
            </div>
            <div className="col-12 col-lg-3 d-flex align-items-end">
              <button className="ce-btn ce-btn--primary ce-btn--sm" onClick={triggerLivePreview} disabled={running || !sbomId}>
                {running ? t('intelligencePreview.running') : t('intelligencePreview.runLivePreview')}
              </button>
            </div>
          </div>

          <div className="small text-secondary mt-3">{data?.note}</div>
          {data?.scanId && (
            <div className="small text-secondary mt-2">
              {t('intelligencePreview.lastRunLabel')}: <span className="font-monospace">{data.scanId}</span>
              {data?.updatedAt ? ` • ${t('intelligencePreview.lastUpdatedLabel')}: ${data.updatedAt}` : ''}
            </div>
          )}
        </div>
      </div>

      <div className="row g-3">
        <MetricCard label={t('intelligencePreview.summary.rule')} value={data?.rule?.name || data?.rule?.id || '—'} />
        <MetricCard label={t('intelligencePreview.summary.status')} value={preview.status || '—'} />
        <MetricCard label={t('intelligencePreview.summary.target')} value={data?.targetType || '—'} />
        <MetricCard label={t('intelligencePreview.summary.findings')} value={preview.execution?.findings ?? 0} />
        <MetricCard label={t('intelligencePreview.summary.assertions')} value={`${preview.evaluation?.expectedAssertionsPassed ?? 0}/${assertionTotal}`} />
        <MetricCard label={t('intelligencePreview.summary.persisted')} value={data?.scanId ? t('intelligencePreview.persistedYes') : '—'} />
      </div>

      <div className="ce-card">
        <div className="ce-card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h6 className="fw-semibold mb-1">{t('intelligencePreview.historyTitle')}</h6>
                  <div className="small text-secondary">{t('intelligencePreview.historySubtitle')}</div>
                </div>
              </div>

          {history.length === 0 ? (
            <div className="small text-secondary">{t('intelligencePreview.historyEmpty')}</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {history.map((entry) => {
                const selected = entry.scanId === data?.scanId;
                return (
                  <button
                    key={entry.scanId}
                    type="button"
                    className={`btn text-start border ${selected ? 'btn-light border-primary' : 'btn-white'}`}
                    onClick={() => selectHistoryRun(entry.scanId)}
                    disabled={loading || running || selected}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div>
                        <div className="fw-semibold small font-monospace">{entry.scanId}</div>
                          <div className="small text-secondary">
                          {entry.createdAt || '—'} • {entry.targetType || '—'} • {entry.demo ? t('intelligencePreview.demoBadge') : t('intelligencePreview.liveBadge')}
                          </div>
                        </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <span className={`ce-sev ce-sev--${decisionTone[entry.promotionDecision] === 'success' ? 'low' : decisionTone[entry.promotionDecision] === 'warning' ? 'medium' : decisionTone[entry.promotionDecision] === 'danger' ? 'critical' : 'none'}`}>{entry.promotionDecision || entry.status || 'unknown'}</span>
                        <span className="ce-tag">{t('intelligencePreview.historyFindings', { count: entry.findings ?? 0 })}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-xl-5">
          <div className="ce-card h-100">
            <div className="ce-card-body">
              <h6 className="fw-semibold mb-3">{t('intelligencePreview.ruleTitle')}</h6>
              <DetailRow label={t('intelligencePreview.ruleFields.id')} value={data?.rule?.id} mono />
              <DetailRow label={t('intelligencePreview.ruleFields.version')} value={data?.rule?.version} />
              <DetailRow label={t('intelligencePreview.ruleFields.status')} value={data?.rule?.status} />
              <DetailRow label={t('intelligencePreview.ruleFields.owner')} value={data?.rule?.owner} />
              <DetailRow label={t('intelligencePreview.ruleFields.category')} value={data?.rule?.category} />
              <DetailRow label={t('intelligencePreview.ruleFields.severity')} value={data?.rule?.severity} />
              <DetailRow label={t('intelligencePreview.ruleFields.defaultConfidence')} value={data?.rule?.defaultConfidence} />
              <DetailRow label={t('intelligencePreview.ruleFields.languages')} value={joinValues(data?.rule?.languages)} />
              <DetailRow label={t('intelligencePreview.ruleFields.cwe')} value={joinValues(data?.rule?.cwe)} />
              <DetailRow label={t('intelligencePreview.ruleFields.owasp')} value={joinValues(data?.rule?.owasp)} />
              <DetailRow label={t('intelligencePreview.ruleFields.remediationKinds')} value={joinValues(data?.rule?.remediationKinds)} />
              <DetailRow label={t('intelligencePreview.ruleFields.evidenceKinds')} value={joinValues(data?.rule?.evidenceKinds)} />
              <DetailRow label={t('intelligencePreview.ruleFields.tags')} value={joinValues(data?.rule?.tags)} />
              <div className="small text-secondary mt-3">{data?.rule?.description}</div>
              {data?.rule?.remediation && (
                <div className="small mt-3 p-3 rounded border bg-body-tertiary">
                  <div className="fw-semibold mb-1">{t('intelligencePreview.remediationTitle')}</div>
                  <div>{data.rule.remediation}</div>
                </div>
              )}

              {(preview.validation?.warnings || []).length > 0 && (
                <div className="mt-3">
                  <div className="small fw-semibold text-secondary mb-2">{t('intelligencePreview.validationWarnings')}</div>
                  <ul className="small ps-3 mb-0">
                    {preview.validation.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-7">
          <div className="ce-card h-100">
            <div className="ce-card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                <div>
                  <h6 className="fw-semibold mb-1">{t('intelligencePreview.findingsTitle')}</h6>
                  <div className="small text-secondary">
                    {t('intelligencePreview.findingsSubtitle', { ruleName: data?.rule?.name || data?.rule?.id || selectedRule?.name || '—' })}
                  </div>
                </div>
              </div>

              {findings.length === 0 ? (
                <div className="text-secondary small">{t('intelligencePreview.noFindings')}</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {findings.map((finding) => (
                    <div key={finding.id} className="ce-card ce-card-body">
                      <div className="d-flex justify-content-between gap-3 flex-wrap align-items-start">
                        <div>
                          <div className="fw-semibold">{finding.title}</div>
                          <div className="small text-secondary">{finding.summary}</div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                          {finding.severity && <span className="ce-sev ce-sev--critical">{finding.severity}</span>}
                          {finding.confidence && <span className="ce-tag">{finding.confidence}</span>}
                        </div>
                      </div>

                      <div className="row g-3 mt-1">
                        <div className="col-12 col-lg-6">
                          <div className="small text-secondary">{t('intelligencePreview.findingFields.path')}</div>
                          <div className="small font-monospace text-break">{finding.path || '—'}</div>
                        </div>
                        <div className="col-12 col-lg-6">
                          <div className="small text-secondary">{t('intelligencePreview.findingFields.locations')}</div>
                          <div className="small">
                            {finding.sourceLocation || '—'} {finding.sinkLocation ? `→ ${finding.sinkLocation}` : ''}
                          </div>
                        </div>
                      </div>

                      {(finding.evidence || []).length > 0 && (
                        <div className="mt-3">
                          <div className="small fw-semibold text-secondary mb-2">{t('intelligencePreview.evidenceTitle')}</div>
                          <ul className="small ps-3 mb-0">
                            {finding.evidence.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="col-6 col-lg-3">
      <div className="ce-card h-100">
        <div className="ce-card-body" style={{ textAlign: 'center' }}>
          <div className="ce-kpi-value" style={{ fontSize: '1.1rem' }}>{value}</div>
          <div className="ce-kpi-label">{label}</div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono = false }) {
  return (
    <div className="d-flex justify-content-between gap-3 py-2 border-bottom small">
      <span className="text-secondary">{label}</span>
      <span className={mono ? 'font-monospace text-break text-end' : 'text-end'}>{value || '—'}</span>
    </div>
  );
}

function joinValues(values) {
  if (!Array.isArray(values) || values.length === 0) return '';
  return values.join(' • ');
}
